import { isArray, isPlainObject } from 'lodash'
import { mergeWith } from 'immutable'

export function filterKeys(o, filterFn) {
  return Object.keys(o)
    .filter((key, i) => filterFn(key, o, i))
    .reduce((a, key) => {
      a[key] = o[key]
      return a
    }, {})
}

export function compare(a, b) {
  return a < b ? -1 : a > b ? 1 : 0
}

export function toSnakeCase(o) {
  return mapObjectKeysRecursive(o, toSnake)

  function toSnake(s) {
    return s
      .replace(
        /([A-Z]+)([^A-Z]+|[A-Z]*$)/g,
        (substring, prefix, postfix) =>
          `_${
            prefix.length > 1 && postfix
              ? prefix.slice(0, prefix.length - 1) +
                '_' +
                prefix.charAt(prefix.length - 1) +
                postfix
              : substring
          }`
      )
      .replace(
        /([^0-9])([0-9]+([^0-9]+|[0-9]*)$)/g,
        (substring, prefix, postfix) => `${prefix}_${postfix}`
      )
      .toLowerCase()
  }
}

export function toCamelCase(o) {
  return mapObjectKeysRecursive(o, toCamel)

  function toCamel(s) {
    return s.replace(
      /_[^_]+/g,
      substring => substring.charAt(1).toUpperCase() + substring.slice(2)
    )
  }
}

function mapObjectKeysRecursive(o, map, stack = []) {
  const isObject = typeof o === 'object'
  if (isObject) {
    if (stack.some(s => s === o)) {
      throw new Error('Circular reference detected')
    }
    stack.push(o)
  }

  try {
    if (isArray(o)) {
      return o.map(o => mapObjectKeysRecursive(o, map, stack))
    } else if (isObject) {
      const result = {}
      for (const key in o) {
        const val = o[key]
        result[map(key)] =
          val && typeof val === 'object'
            ? mapObjectKeysRecursive(val, map, stack)
            : val
      }

      return result
    } else {
      return o
    }
  } finally {
    if (isObject) {
      stack.pop()
    }
  }
}

/**
  This attempts to merge two objects recursively:
  plain objects will be merged recursively key by key,
  where only keys available in a _and_ b will be overwritten,
  any keys available in `a` or its child objects will
  be preserved.

  For lists or non-plain objects, values from `b` will
  overwrite `a`.

  A new object is returned, `a` and `b` are not mutated.

  This (or at least very similar to) an immutable version
  of lodash's `merge` function

  @param a {any} object to merge to
  @param b {any} object to merge from
  @returns a new, merged object
*/
export function recursiveMerge(a, b) {
  return mergeWith(
    (oldValue, newValue) => {
      if (oldValue == null) {
        return newValue
      }

      if (typeof newValue === 'object') {
        if (
          isArray(newValue) ||
          isArray(oldValue) ||
          !isPlainObject(newValue)
        ) {
          return newValue
        } else {
          return recursiveMerge(oldValue, newValue)
        }
      } else {
        return newValue
      }
    },
    a,
    b
  )
}
